/**
 * Request-to-book basket classification — the one copy, shared by the API's
 * checkout (which reads `requiresApproval` from its own V2 ticket rows) and
 * the checkout pages in @baluu/ui (which read the flag stamped on each ticket
 * selection when the item was added).
 *
 * A basket is either entirely request-to-book (every line is a booking whose
 * V2 ticket type has `requiresApproval`) or not request-to-book at all.
 * Anything else is "mixed" and is refused at checkout: a request order
 * collects no money up front, so it cannot share a payment with items that
 * must be charged now.
 *
 * Lines: a booking item can carry several ticket selections, so each
 * selection is its own line — one request ticket and one standard ticket on
 * the same booking is "mixed". Zero-quantity selections are ignored. Every
 * non-booking item (product, gift card, bundle), and a booking item with no
 * selections, is one line with no selection, which never counts as request.
 */
export type RequestToBookClass = "all" | "none" | "mixed";

export type RequestToBookSelection = {
  ticketTypeId?: string | null;
  quantity?: number | null;
  /** The ticket's flag as stamped by the checkout page (display hint only). */
  requiresApproval?: boolean | null;
};

export type RequestToBookItem<S extends RequestToBookSelection = RequestToBookSelection> = {
  type?: string;
  ticketSelections?: ReadonlyArray<S> | null;
};

export type RequestToBookLine<S extends RequestToBookSelection = RequestToBookSelection> = {
  type?: string;
  /** Null for a non-booking item or a booking with no selections. */
  selection: S | null;
};

/** Flattens basket items into classifier lines (see the module comment). */
export function toRequestToBookLines<S extends RequestToBookSelection>(
  items: ReadonlyArray<RequestToBookItem<S>>
): Array<RequestToBookLine<S>> {
  return items.flatMap((item): Array<RequestToBookLine<S>> => {
    const selections =
      item.type === "booking"
        ? (item.ticketSelections ?? []).filter((ts) => (ts.quantity ?? 1) > 0)
        : [];
    if (selections.length === 0) return [{ type: item.type, selection: null }];
    return selections.map((selection) => ({ type: item.type, selection }));
  });
}

/** The selection's stamped flag — what the checkout pages classify on. */
export const selectionRequiresApproval = (selection: RequestToBookSelection): boolean =>
  !!selection.requiresApproval;

/**
 * Classifies basket items. `isRequestSelection` answers "is this selection's
 * ticket request-to-book?": the server passes a lookup into its V2 ticket
 * rows (the source of truth — a stamp is never trusted for money); the
 * default reads the stamp, for the checkout pages.
 */
export function classifyRequestToBookBasket<S extends RequestToBookSelection>(
  items: ReadonlyArray<RequestToBookItem<S>>,
  isRequestSelection: (selection: S) => boolean = selectionRequiresApproval
): RequestToBookClass {
  let request = 0;
  let other = 0;
  for (const line of toRequestToBookLines(items)) {
    if (line.type === "booking" && line.selection && isRequestSelection(line.selection)) {
      request += 1;
    } else {
      other += 1;
    }
  }
  if (request === 0) return "none";
  return other === 0 ? "all" : "mixed";
}
